import { ArrowRightIcon, MessageCircleIcon } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { TASK_TYPE_CONFIG } from '../data/task-config'
import {
  COMMENT_ASPECT_LABELS,
  type CoachComment,
  type HomeworkTask,
} from '../data/types'
import { useHomework } from '../providers/homework-provider'

/** Coach avatar — initials fallback (no static image asset in this app) */
export function CoachAvatar({ name = 'Coach Maya' }: { name?: string }) {
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <span
      className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary ring-1 ring-border"
      aria-hidden="true"
    >
      {initials}
    </span>
  )
}

/** Coach face + speech bubble, shared look with the task detail dialog */
export function CoachBubble({
  author,
  text,
  aspect,
  meta,
}: {
  author: string
  text: string
  aspect?: CoachComment['aspect']
  meta?: ReactNode
}) {
  return (
    <div className="flex items-start gap-2">
      <CoachAvatar name={author} />
      <div className="relative min-w-0 flex-1 rounded-lg rounded-tl-none border bg-muted/40 px-2.5 py-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-xs font-semibold">{author}</span>
          {aspect && (
            <span className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
              on your {COMMENT_ASPECT_LABELS[aspect]}
            </span>
          )}
          {meta}
        </div>
        {/* Coach notes are multi-paragraph free text; collapsing newlines here
            would run a numbered list into one sentence. */}
        <p className="mt-0.5 text-sm leading-relaxed whitespace-pre-line text-pretty">
          {text}
        </p>
      </div>
    </div>
  )
}

/**
 * "commented on your post-mortem for your 3Sum solution" — lead with the
 * specific aspect. Multiple aspects collapse to "post-mortem and code".
 */
function headline(task: HomeworkTask): ReactNode {
  const coach = task.comments[0]?.author ?? 'Your coach'
  const aspects = [...new Set(task.comments.map((c) => c.aspect))]
  const aspectText =
    aspects.length === 1
      ? COMMENT_ASPECT_LABELS[aspects[0]]
      : aspects
          .map((a) => COMMENT_ASPECT_LABELS[a])
          .slice(0, 2)
          .join(' and ')
  const generalOnly = aspects.length === 1 && aspects[0] === 'general'

  return (
    <>
      <span className="font-semibold">{coach}</span>{' '}
      <span className="text-muted-foreground">commented on your </span>
      {generalOnly ? (
        <>
          <span className="font-medium">{task.title}</span>
          <span className="text-muted-foreground"> solution</span>
        </>
      ) : (
        <>
          <span className="font-medium">{aspectText}</span>
          <span className="text-muted-foreground"> for your </span>
          <span className="font-medium">{task.title}</span>
          <span className="text-muted-foreground"> solution</span>
        </>
      )}
    </>
  )
}

function FeedbackGroup({ task }: { task: HomeworkTask }) {
  const { openTask, isCommentsRead } = useHomework()
  const config = TASK_TYPE_CONFIG[task.type]
  const Icon = config.icon
  const unread = !isCommentsRead(task)

  return (
    <div
      className={cn(
        'flex flex-col gap-2 border-l-2 py-1 pl-3',
        unread ? 'border-primary' : 'border-border',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded',
            config.chip,
          )}
        >
          <Icon className="size-3" aria-hidden="true" />
        </span>
        <p className="min-w-0 flex-1 text-sm leading-snug">{headline(task)}</p>
        {unread && (
          <Badge className="shrink-0 px-1.5 py-0 text-[10px]">New</Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openTask(task)}
          className="h-6 shrink-0 px-2 text-xs"
        >
          View solution
          <ArrowRightIcon />
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        {task.comments.map((comment) => (
          <CoachBubble
            key={comment.id}
            author={comment.author}
            text={comment.text}
            aspect={comment.aspect}
          />
        ))}
      </div>
    </div>
  )
}

export function useGradedTasks() {
  const { tasks, isCommentsRead } = useHomework()

  // Group by problem: unread feedback first, then most recent activity.
  return useMemo(() => {
    return tasks
      .filter((t) => t.comments.length > 0)
      .sort((a, b) => {
        const aUnread = isCommentsRead(a) ? 1 : 0
        const bUnread = isCommentsRead(b) ? 1 : 0
        if (aUnread !== bUnread) return aUnread - bUnread
        const aLast = a.comments[a.comments.length - 1]?.date ?? ''
        const bLast = b.comments[b.comments.length - 1]?.date ?? ''
        return bLast.localeCompare(aLast)
      })
  }, [tasks, isCommentsRead])
}

export function CoachFeedback() {
  const graded = useGradedTasks()

  if (graded.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed px-4 py-8 text-center">
        <MessageCircleIcon
          className="size-5 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">
          No graded work yet — feedback from your coach will show up here.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border p-4">
      {graded.map((task) => (
        <FeedbackGroup key={task.id} task={task} />
      ))}
    </div>
  )
}
